import { defineComponent as _defineComponent } from 'vue'
export interface SiteAnnouncement {
  title: string;
  details?: string;
}


import { ref, computed, watch, onMounted, onUnmounted } from 'vue';

import { auth } from '@auth';
import { modals } from '@modals';
import { useRoute, useRouter } from '@router';
import { useStore, useGetter } from '@store';
import { externalLinks, type ExternalSiteLink } from '@values/external-links';

import CartWidget from '@components/CartWidget.vue';
import SidebarMobile from '@components/SidebarMobile.vue';
import ZephyrLogo from '@components/SVGs/ZephyrLogo.vue';

import { faArrowCircleDown } from '@icons/regular/faArrowCircleDown';
import { faShoppingCart } from '@icons/regular/faShoppingCart';
import { faTachometerFast } from '@icons/regular/faTachometerFast';
import { faUsers } from '@icons/regular/faUsers';
// import { faDisplay } from '@icons/regular/faDisplay';
// import { faSignOut } from '@icons/regular/faSignOut';
import { faBell } from '@icons/regular/faBell';
import { faCog } from '@icons/regular/faCog';
import { faVial } from '@icons/regular/faVial';
// import { faPalette } from '@icons/regular/faPalette';

import AnnouncementsDropdown from './AnnouncementsDropdown.vue';
// import AppearanceDropdown from './AppearanceDropdown.vue';
import MobileMenuButton from './MobileMenuButton.vue';
import NavItemIcon from './NavItemIcon.vue';
// import RouteLoadProgressBar from './RouteLoadProgressBar.vue';
import SiteAnnouncement from './SiteAnnouncement.vue';

type AnnouncementsDropdownRef = InstanceType<typeof AnnouncementsDropdown>;

interface RouteMenuItem {
  id: string;
  title: string;
  route: string;
}

type MenuItem = RouteMenuItem | ExternalSiteLink;

const NAV_MENU_ITEMS: MenuItem[] = [
  externalLinks.faqs,
  // { id: 'about', title: 'About', route: 'about' },
  { id: 'features', title: 'Features', route: 'features' },
  { id: 'individuals', title: 'Individuals', route: 'individuals' },
  { id: 'enterprise', title: 'Enterprise', route: 'enterprise' },
  { id: 'certifications', title: 'Certifications', route: 'certifications' },
  // { id: 'news', title: 'News', route: 'news' },
  { id: 'shop', title: 'Shop', route: 'shop' },
  // { id: 'pricing', title: 'Pricing', route: 'pricing' },
];

const BASE_MENU_ITEM = ['faqs', 'features', 'certifications'];
const AUTH_MENU_ITEMS = [...BASE_MENU_ITEM, 'shop'];
const NO_AUTH_MENU_ITEMS = [...BASE_MENU_ITEM, 'individuals', 'enterprise'];

const __default__ = { name: 'AppHeader' };

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

const announcementsDropdown = ref<AnnouncementsDropdownRef | null>(null);
const siteAnnouncement = ref<SiteAnnouncement | null>(null);

const store = useStore();
const router = useRouter();
const route = useRoute();

const collapsed = ref(true);
const atPageTop = ref(window.scrollY <= 0);

const displayMode = computed(() => {
  return store.state.displayMode;
});
const initialNavigationCompleted = computed(() => {
  return store.state.initialNavigationCompleted;
});
const launcherLinks = computed(() => {
  return store.state.launcherLinks;
});
const cart = computed(() => {
  return store.state.cart;
});

const selectableRoles = useGetter('me/selectableRoles');

watch(
  () => route.value.name,
  () => {
    collapsed.value = true;
  },
);

const renderNavItems = computed(() => {
  return initialNavigationCompleted.value;
});

const menu = computed(() => {
  const itemsFilter = auth.user ? AUTH_MENU_ITEMS : NO_AUTH_MENU_ITEMS;

  const items = NAV_MENU_ITEMS.filter(({ id }) => {
    if (!itemsFilter.includes(id)) {
      return false;
    }

    if (id === 'shop' && auth.user?.isK12) {
      return false;
    }

    return true;
  });

  // items.push({ id: 'testing', title: 'Testing', state: 'testing' });

  return items;
});

/** ... */
const launcherLink = computed(() => {
  return launcherLinks.value[0]?.link ?? '';
});

const classList = computed(() => {
  const classList: string[] = [];

  if (
    route.value.name !== 'lasadmin' &&
    route.value.name !== 'admin' &&
    route.value.name !== 'instructor' &&
    route.value.name !== 'student' &&
    route.value.name !== 'subscriber' &&
    route.value.name !== 'independentOperator'
  ) {
    classList.push('pinned');
  }

  if (isTransparent.value) {
    classList.push('transparent');
  }

  if (isMobile.value) {
    classList.push('mobile');

    if (!collapsed.value) {
      classList.push('expanded');
    }
  }

  return classList;
});

/** ... */
const cartItemCount = computed(() => {
  let count = 0;

  for (const item of cart.value.items) {
    count += item.quantity;
  }

  return count;
});

/** ... */
const isMobile = computed(() => {
  return displayMode.value === 'mobile';
});

/** ... */
const canChangeRoles = computed(() => {
  return selectableRoles.value.length > 1;
});

/** ... */
const showDownloadButton = computed(() => {
  return auth.user && !!launcherLink.value;
});

const showCartWidget = computed(() => {
  return !auth.user?.isK12 && route.value.name !== 'preCheckout';
});

/** ... */
const isTransparent = computed(() => {
  // ...
  if (!atPageTop.value) return false;

  // ...
  return ['main', 'mainV2', 'individuals', 'enterprise'].some(
    (name) => name === route.value.name,
  );
});

/** ... */
const isHidden = computed(() => {
  return (
    !auth.initialized ||
    route.value.name === null ||
    route.value.name === 'loginCallback' ||
    route.value.name === 'onboarding'
  );
});

/** ... */
const announcements = computed(() => {
  return store.state.announcements.items;
});

/** ... */
const unreadAnnouncementsCount = computed(() => {
  return announcements.value.filter(({ read }) => !read).length;
});

onMounted(() => {
  // Previously from 'created' hook.
  /* eslint-disable @typescript-eslint/unbound-method */
  window.addEventListener('load', onScreenPosUpdated);
  window.addEventListener('resize', onScreenPosUpdated);
  window.addEventListener('scroll', onScreenPosUpdated);
  /* eslint-enable @typescript-eslint/unbound-method */

  // siteAnnouncement.value = {
  //   title: 'Zephyr will undergo maintenance Nov. 19-20',
  //   details:
  //     'On Saturday, November 19th at 6pm EST to Sunday, November 20th at 6pm EST, Zephyr will undergo scheduled maintenance.<br /><br />During time.value, you may not be able access the Zephyr website or you may see incomplete or missing data. If you experience this, please try again after Sunday November 20th at 6pm EST before reaching out to our support.<br /><br />You may also experience issues submitting report data from the simulator. If you experience this, your reports will be saved locally. The next time you log in to the simulator, they will be automatically resubmitted.',
  // };
});

onUnmounted(() => {
  /* eslint-disable @typescript-eslint/unbound-method */
  window.removeEventListener('load', onScreenPosUpdated);
  window.removeEventListener('resize', onScreenPosUpdated);
  window.removeEventListener('scroll', onScreenPosUpdated);
  /* eslint-enable @typescript-eslint/unbound-method */
});

//#region Route Navigation

function goToRoute(name: string) {
  if (router.currentRoute.name !== name) {
    void router.push({ name });
  }
}

// const goToSignUp = () => goToRoute('checkout');
const goToDashboard = () => goToRoute('dashboard');
const goToIndividuals = () => goToRoute('individuals');
// const goToEnterprise = () => goToRoute('enterprise');
const goToCart = () => goToRoute('cart');
// const goToShop = () => goToRoute('shop');
const goToTesting = () => goToRoute('testing');

//#endregion Route Navigation

//#region Modal Openers

/** Open the role selection modal. */
const openRoleSelect = () => {
  void modals.account.selectRole();
};

// /** Open app settings modal. */
// const openSettingsModal = () => {
//   void modals.settings.appSettings();
// };

/** Open user settings modal. */
const openUserSettingsModal = () => {
  void modals.account.settings();
};

//#endregion Modal Openers

//#region Account/Authorization

const login = () => {
  void auth.loginWithRedirect({
    prompt: 'login',
    returnTo: { name: 'dashboard' },
  });
};

const logout = () => {
  void auth.logout();
};

//#endregion Account/Authorization

const toggleAnnouncementsDropdown = () => {
  announcementsDropdown.value?.toggle();
};

const onScreenPosUpdated = () => {
  atPageTop.value = window.scrollY <= 0;
};

return { __sfc: true,NAV_MENU_ITEMS, BASE_MENU_ITEM, AUTH_MENU_ITEMS, NO_AUTH_MENU_ITEMS, announcementsDropdown, siteAnnouncement, store, router, route, collapsed, atPageTop, displayMode, initialNavigationCompleted, launcherLinks, cart, selectableRoles, renderNavItems, menu, launcherLink, classList, cartItemCount, isMobile, canChangeRoles, showDownloadButton, showCartWidget, isTransparent, isHidden, announcements, unreadAnnouncementsCount, goToRoute, goToDashboard, goToIndividuals, goToCart, goToTesting, openRoleSelect, openUserSettingsModal, login, logout, toggleAnnouncementsDropdown, onScreenPosUpdated, auth, CartWidget, SidebarMobile, ZephyrLogo, faArrowCircleDown, faShoppingCart, faTachometerFast, faUsers, faBell, faCog, faVial, AnnouncementsDropdown, MobileMenuButton, NavItemIcon, SiteAnnouncement }
}

})